/** Marks the tour's own UI so modal detection and target lookups ignore it. */
export const TOUR_UI_ATTR = 'data-tour-ui';

/** A target counts only when it is rendered with a non-zero size. */
export function isVisibleElement(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

export function findVisible(selector: string): HTMLElement | null {
  const matches = document.querySelectorAll<HTMLElement>(selector);
  for (const el of matches) {
    if (!el.closest(`[${TOUR_UI_ATTR}]`) && isVisibleElement(el)) return el;
  }
  return null;
}

/**
 * Resolves with the first visible match, or `null` after `timeout` ms or when
 * `signal` aborts. Watches DOM changes and also re-checks on a short interval,
 * since a layout change can reveal an element without any mutation.
 */
export function waitForElement(
  selector: string,
  { timeout = 4000, signal }: { timeout?: number; signal?: AbortSignal } = {},
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const existing = findVisible(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    let done = false;
    const finish = (el: HTMLElement | null) => {
      if (done) return;
      done = true;
      observer.disconnect();
      window.clearInterval(interval);
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(el);
    };
    const check = () => {
      const el = findVisible(selector);
      if (el) finish(el);
    };
    const onAbort = () => finish(null);

    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'data-tour'],
    });
    const interval = window.setInterval(check, 200);
    const timer = window.setTimeout(() => finish(null), timeout);
    signal?.addEventListener('abort', onAbort);
  });
}

/**
 * Whether a dialog or full-screen overlay is open. Most modals here are
 * `fixed inset-0` backdrops rather than `role="dialog"`, so both are checked.
 */
export function isBlockingModalOpen(): boolean {
  const candidates = document.querySelectorAll<HTMLElement>(
    '[role="dialog"], [aria-modal="true"], .fixed.inset-0',
  );
  for (const el of candidates) {
    if (el.closest(`[${TOUR_UI_ATTR}]`)) continue;
    if (isVisibleElement(el)) return true;
  }
  return false;
}

export function isFullyInView(el: Element, inset = 0): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= inset &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight - inset &&
    rect.right <= window.innerWidth
  );
}

/** Whether the element, or an ancestor, is fixed or sticky and so can't be scrolled to. */
export function isPinned(el: Element): boolean {
  let node: Element | null = el;
  while (node && node !== document.body) {
    const position = window.getComputedStyle(node).position;
    if (position === 'fixed' || position === 'sticky') return true;
    node = node.parentElement;
  }
  return false;
}
